import { supabase } from '../lib/supabase';

/**
 * Get all shipments
 * @returns {Promise<Array>} List of shipments
 */
const getShipments = async () => {
  try {
    const { data, error } = await supabase
      ?.from('shipments')
      ?.select(`
        id,
        reference,
        sender_name,
        sender_phone,
        sender_city,
        receiver_name,
        receiver_phone,
        receiver_city,
        current_status,
        created_at
      `)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    // Convert snake_case to camelCase
    return (data || [])?.map(shipment => ({
      id: shipment?.id,
      reference: shipment?.reference,
      senderName: shipment?.sender_name,
      senderPhone: shipment?.sender_phone,
      senderCity: shipment?.sender_city,
      recipientName: shipment?.receiver_name,
      recipientPhone: shipment?.receiver_phone,
      recipientCity: shipment?.receiver_city,
      status: shipment?.current_status,
      createdAt: shipment?.created_at
    })) || [];
  } catch (error) {
    console.error('Error fetching shipments:', error);
    throw error;
  }
};

/**
 * Secure public tracking via RPC — no direct table reads on shipments, shipment_items, or shipment_files.
 * Uses the get_public_tracking(p_reference) Supabase function which returns { shipment, items, files }.
 */
const getShipmentByReference = async (reference) => {
  const trimmedRef = reference?.trim();
  console.log('[TRACKING] getShipmentByReference via RPC:', trimmedRef);

  const { data, error } = await supabase?.rpc('get_public_tracking', {
    p_reference: trimmedRef
  });

  if (error) {
    console.error('[TRACKING] RPC get_public_tracking error:', error);
    throw error;
  }

  console.log('RPC tracking data', data);

  if (!data) {
    console.warn('[TRACKING] No shipment found for reference:', trimmedRef);
    return null;
  }

  // Handle the 3 possible return formats from Supabase RPC
  const trackingData =
    data?.shipment
      ? data
      : data?.get_public_tracking
        ? data?.get_public_tracking
        : Array.isArray(data) && data?.[0]?.get_public_tracking
          ? data?.[0]?.get_public_tracking
          : null;

  if (!trackingData || !trackingData?.shipment) {
    console.warn('[TRACKING] RPC returned data but shipment is null for:', trimmedRef, '| raw data:', data);
    return null;
  }

  const shipmentData = trackingData?.shipment;
  const items = trackingData?.items || [];
  const files = trackingData?.files || [];

  // Build parcels_summary from items if not present
  let parcels_summary = shipmentData?.parcels_summary || null;
  if (!parcels_summary && items?.length > 0) {
    if (items?.length === 1) {
      const item = items?.[0];
      const name = item?.item_name || item?.catalog_items?.name || item?.name || null;
      const qty = item?.quantity || 1;
      if (name) {
        parcels_summary = qty > 1 ? `${name} ×${qty}` : name;
      }
    } else {
      const names = items
        ?.map(item => item?.item_name || item?.catalog_items?.name || item?.name)
        ?.filter(Boolean);
      if (names?.length > 0) {
        parcels_summary = names?.join(', ');
      } else {
        parcels_summary = `${items?.length} articles`;
      }
    }
  }

  console.log('[TRACKING] ✅ Shipment found:', shipmentData?.reference, '| Status:', shipmentData?.current_status, '| Files:', files?.length);

  // Extract client loyalty points — may be returned by RPC in various fields
  const clientLoyaltyPoints =
    trackingData?.client_loyalty_points ??
    shipmentData?.client_loyalty_points ??
    shipmentData?.client?.loyalty_points_total ??
    null;

  return {
    ...shipmentData,
    status: shipmentData?.current_status,
    items: items,
    files: files,
    parcels_summary: parcels_summary,
    container: shipmentData?.containers || null,
    tracking_events: trackingData?.tracking_events || [],
    client_loyalty_points: clientLoyaltyPoints,
    loyalty_points_earned: shipmentData?.loyalty_points_earned ?? 0
  };
};

// Export as object with all methods
export const shipmentService = {
  getShipments,
  getShipmentByReference
};

// Also export individual functions for direct imports
export { getShipments, getShipmentByReference };